
export interface OrderSummaryPage {
    orderSummaryDetails: OrderSummaryDetails;
    orderSummaryPageLabels: OrderSummaryPageLabels;
    csrfToken: CsrfToken;
    alerts: Alert[];
    medicationGroupCard: MedicationGroupCard;
    refundDetails: OrderSummaryRefundDetails;
    insuranceInformation: InfoGroup;
    paymentInformation: InfoGroup;
    invoiceMenu: MedicationGroup[];
    cancelDetails: CancelBottomSheetDetails;
    bypassTracker?: boolean;
    isAddPaymentMethodAllowed?: boolean;
    isPillPackComOrder: boolean;
    isPendingCost?: boolean;
    isBulkOrder?: boolean;
    shiplitIds?: [string];
	isMShop?: boolean;
    isIosDevice?: boolean;
    isAndroidDevice?: boolean;
    isPickUpOrder?: boolean;
}

export interface CancelBottomSheetDetails{
    csrfToken:CsrfToken;
    orderIds:string[];
    purchaseId: string;
    cancellationGroup:MedicationGroup;
    cancellableMedications?: MedicationGroup[];
}

export interface Article {
    headline: string;
    sublines: string[];
}

export interface InfoGroup {
    id: string;
    annotations: Annotation[]
    items: InfoItem[]
}

export interface InfoItem {
    id: string;
    label: string;
    annotations?: Annotation[];
}

export interface OrderSummaryRefundDetails {
    refundDetails: string;
    total: RefundAmount;
    refundBottomSheetDetails: RefundBottomSheetDetails;
}

export interface MedicationGroupCard {
    medicationGroupHeader: MedicationGroup;
    medicationGroupList: MedicationGroup[];
}

export interface MedicationGroup {
    id?: string;
    orderId?: string;
    invoiceId?: string;
    drugType?: "OTC" | "PRESCRIPTION";
    patientDrugRegimenType?: PatientDrugRegimenType;
    asin?: string;
    drugId?: string;
    headline: string;
    photoUrl?: string;
    subline: string;
    priceString?: string;
    paymentInformation?: string;
    promotionSavingLine?: string;
    autoRefillLine?: string;
    annotations: Annotation[];
}

export interface OrderSummaryDetails {
    id: string;
    idLabel: string;
    invoiceId: string;
    date: string;
    dateLabel: string;
    alert: string;
    medicationCount: string;
    charges: Charge;
}

export interface OrderSummary {
    order: Order;
}

export interface Invoice {
    order: Order;
}

/**
 * Type of drug - RX is the default when not specified
 * TODO: remove this type post-OTC launch. SIM: https://sim.amazon.com/issues/potux-1760
 */
export enum DrugType {
    OTC = "OTC",
    RX = "RX"
}

export enum PatientDrugRegimenType {
    NON_PRESCRIPTION = "NON_PRESCRIPTION",
    PRESCRIPTION = "PRESCRIPTION"
}

export type AnnotationType =
  | "None"
  | "PayFixup"
  | "UpdatePaymentMethod"
  | "Cancel"
  | "ReplacedBy"
  | "Replaces"
  | "ItemCancelledOn"
  | "SuppressPayFixup"
  | "MedsSyncing"
  | "MedsSynced"
  | "Refunded"
  | "Reversed"
  | "Refund"
  | "BackupPaymentMethod"
  | "PillPackPartialCancel"
  | "HbaPaymentDecline"
  | "ManufacturerChanged"

export interface Annotation {
  label: string;
  labelTitle?: string;
  color?: string;
  fontStyle?: string;
  annotationType: AnnotationType;
}

export interface PaymentScenario {
    paymentMethod: "Cash" | "Copay";
    label: string;
}

export interface PaymentStatus {
    paymentStatus: "Pending" | "Approved" | "Failed" | "Unknown";
    label: string;
}

export interface Transaction {
    id?: string;
    label?: string;
    brand: string
    tail: string
    scenario: PaymentScenario
    address: Address
    when: HumanizedDate
    charge: string
    status: PaymentStatus
    annotations: Annotation[]
    originalPaymentMethod?: OriginalPaymentMethod;
}

export interface ShipSpeed {
    description?: string;
    duration?: string;
    descriptionId?: string
    durationId?: string
    maximumTransitTimeDisplayUnit?: string
    minimumTransitTimeDisplayUnit?: string
}

export interface PaymentSummary {
    label: string;
    summary: string;
}

export interface Order {
    id: string;
    orderId: string;
    purchaseId: string;
    items: Item[];
    orderedOn: HumanizedDate;
    charges: Charge;
    headline: string;
    subline: string;
    delivery?: Address;
    isPickUpOrder: boolean;
    hbaEligibleAmount: string;
    hbaEligibility: HBAEligibility;
    orderDate: HumanizedDate;
    status: OrderStatus;
    transactions: Transaction[];
    csrf?: CsrfToken;
    orderingChannel: string;
    isUpdatePaymentMethodAllowed: boolean;
    orderDetailPageLabels: OrderDetailPageLabels;
    invoicePageLabels: InvoicePageLabels;
    isAutoRefill: boolean;
    orderingChannelLine: string;
    reverseRebillOrderDetails?: ReverseRebillOrderDetails;
    bypassTracker: boolean;
}

export interface OrderDetailPageLabels {
    yourOrders: string;
    delivery: string;
    tracking: string;
    order: string;
    id: string;
    orderDate: string;
    cancelForm: CancelFormLabels;
    redirectCancellationMessages: RedirectCancellationMessages
    paymentMethod: string;
    hsaFsaCard: string;
    paymentInformation: string;
    updatePaymentMethod: string;
    help: string;
    viewInvoice: string;
    viewInvoiceMenu: string;
    invoiceMenuTitle: string;
    contactCustomerCare: string;
    insuranceAndDiscountCardInformation: string;
    refund: string;
    refundOn: string;
    refundInformation: string;
    refundTotal: string;
    taxRefund: string;
    paymentLabels: PaymentLabels;
}

export interface OrderSummaryPageLabels {
    yourOrders: string;
    delivery: string;
    tracking: string;
    order: string;
    orderSummary: string;
    id: string;
    orderDate: string;
    cancelForm: CancelFormLabels;
    redirectCancellationMessages: RedirectCancellationMessages
    paymentMethod: string;
    hsaFsaCard: string;
    paymentInformation: string;
    updatePaymentMethod: string;
    help: string;
    viewInvoice: string;
    viewInvoiceMenu: string;
    invoiceMenuTitle: string;
    contactCustomerCare: string;
    insuranceAndDiscountCardInformation: string;
    refund: string;
    refundOn: string;
    refundInformation: string;
    refundTotal: string;
    taxRefund: string;
    viewPillpackSettings: string;
    askAQuestion: string;
    paymentLabels: PaymentLabels;
    insuranceProcessingLabel: string;
    insuranceProcessingMsg: string;
    viewShipmentLiterature?: string;
    downloadingShipmentLiterature?: string;
    downloadingDoneShipmentLiterature?: string;
    downloadingErrorShipmentLiterature?: string;
    continueShipmentLiterature?: string;
    medicationInformationShipmentLiterature?: string;
    pdfNameShipmentLiterature?: string;
}

export interface OriginalPaymentMethod {
    backupPaymentMethodLabels: BackupPaymentMethodLabels
    backupPaymentMethodWarning: string
}

export interface BackupPaymentMethodLabels {
    backupPaymentMethodUsedTitle: string
    backupPaymentMethodInformationalText: string
    learnMore: string
}

export interface InvoicePageLabels {
    yourOrders: string;
    delivery: string;
    tracking: string;
    order: string;
    invoice: string;
    orderId: string;
    orderDate: string;
    shipSpeed: string;
    notApplicable: string;
    payments: string;
    orderInformation: string;
    fsaHsaEligibleAmount: string;
    deliveryAddress: string;
    billingAddress: string;
    ndcId: string;
    itemQuantity: string;
    unitPrice: string;
    patient: string;
    prescriptionId: string;
    prescriber: string;
    insurance: string;
    soldBy: string;
    nabpNpi: string;
    shippingDate: string;
    filledOn: string;
    transactionOn: string;
}

export interface CsrfToken {
    token: string;
    parameter: string;
    header: string;
}

export interface HBAEligibility {
    label: string
    hbaEligible: "Eligible" | "NotEligible"
}

export interface HumanizedDate {
    month: string;
    day: string;
    year: string;
    value: string;
}

export interface ReverseRebillOrderDetails {
    rebillOrderId?: string;
    parentOrderId?: string;
    rebillOrderType?: string;
    parentOrderURL?: string;
}

export interface ItemStatus {
    status: string;
    label?: string;
}

export interface Item {
    id: string;
    itemId: string;
    drugType?: DrugType;
    headline: string;
    subline: string;
    insurances: Insurance[];
    charges: Charge;
    delivery: Delivery;
    rx: Rx;
    hbaEligibility: HbaEligibility;
    soldBy: Seller;
    shipmentDate: HumanizedDate;
    status: ItemStatus;
    delivery: Delivery;
    shipSpeed: ShipSpeed;
    annotations: Annotation[];
    patient?: Patient;
    refunds?: Refund[];
    orderingChannel?: string;
    promotionSavingsLine?: string;
    patientDrugRegimenType?: PatientDrugRegimenType;
}

export interface Seller {
    id: string
    sellerName: string
    address: Address
}

export interface Patient {
    name: string;
}

export interface Delivery {
    carrier: string;
    address: Address;
    status: {
        status: string;
    }
    events: DeliveryEvent[];
    lastStepProgress: number;
    subline: string;
    label: string;
    note: string;
    eta: string;
    labels: DeliveryPageLabels;
    deliveryPhoto?: DeliveryPhoto;
    trackingNumber: string | null;
}

export interface DeliveryPhoto {
    photoUrl: string;
    photoUrlAlt?: string;
    accessibilityLabel?: string;
}

export interface DeliveryPageLabels {
    trackThisDelivery: string;
}

export interface Insurance {
    id: string;
    provider: string;
    providerLabel?: string
    cardHolderId: string
    tailLabel?: string
    claimGroupId: string
    cardKind: CardKind;
    compositeTailLabel?: string;
    payerRank?: "PRIMARY" | "SECONDARY" | "TERTIARY"
}

export interface CardKind {
    type: 'Standard' | 'Medicare' | 'Coupon' | 'PrimeRx' | 'AEB' | 'Blue_Shield_CA' | 'Arrow' | 'Discount' | 'Unknown';
    label: string;
}

export interface HbaEligibility {
    hbaEligible: 'Eligible' | 'NotEligible',
    label: string;
}

export interface Charge {
    unitPrice?: string
    unitPriceLabel?: string
    item: string
    itemLabel: string
    shipping: string
    shippingLabel: string
    couponSaving?: string
    couponSavingLabel?: string
    shippingSaving?: string
    shippingSavingLabel?: string
    totalBeforeTax: string
    totalBeforeTaxLabel: string
    salesTax: string
    salesTaxLabel: string
    grandTotal: string
    grandTotalLabel: string
    hbaEligibleTotal: string
    hbaEligibleTotalLabel: string
    copayLabel?: string
    copay?: string
    pillpackServiceFee?: string
    pillpackServiceFeeLabel?: string
}

export interface DeliveryEvent {
    headline: string;
    subline?: string;
    note?: string;
}

export interface Address {
    lines: string[],
}

export interface PurchaseHistory {
    upcoming: ItemGroupSummary[];
    past: ItemGroupSummary[];
    pillPackDeliveryScheduleCard: PillPackDeliveryScheduleCard;
    labels: PurchaseHistoryPageLabels;
    iosDevice: boolean;
    androidDevice: boolean;
    mShop: boolean;
    isMigratedCustomer: boolean;
}

export interface PillPackDeliveryScheduleCard {
    isUserEnrolledForPillPack: boolean;
    seeFuturePillPackDeliveries?: string;
}

export interface PurchaseHistoryPageLabels {
    yourOrders: string;
    delivery: string;
    itemsInThisDelivery: string;
    deliveryAddress: string;
    seeMoreOrders: string;
    seeMoreOrdersLoading: string;
    seeMoreOrdersFailure: string;
    ordersOlderThan90Days: string;
    questions: string;
    contactCustomerCare: string;
    helpLink: string;
    pastOrders: string;
    pastOrdersSubline: string;
    orderId: string;
    noUpcomingOrders: string;
    getMedicationOrderStatements: string;
    upcomingOrdersTab: string;
    pastOrdersTab: string;
    trackDelivery: string;
    seeDetails: string;
    orderHistory: string;
    orderStatement: string;
    pickUpInstructions: string;
}

export interface DispenseStatement {
    labels: DispenseStatementPageLabels;
    filterOptions: DispenseStatementFilterOption[];
    pillPackComFilterOptions?: PillPackComFilterOption[];
}

export interface DispenseStatementPageLabels {
    medicationStatements: string;
    chooseYear: string;
    selectYearDropdown: string;
    info: string;
    download: string;
    downloadProgress: string;
    downloadSuccess: string;
    downloadError: string;
    continueButton: string;
    downloadAgain: string;
    noStatementError: string;
    amazonPharmacy: string;
    pillPackCom: string;
    pillPackComStatementsInProgress: string;
    pillPackComCheckBackLater: string;
}

export interface DispenseStatementFilterOption {
    label: string;
    value: string;
    parameterList: DispenseStatementFilterOptionParameter[];
}

export interface PillPackComFilterOption {
    label: string;
    value: string;
}

export interface DispenseStatementFilterOptionParameter {
    key: string;
    value: string;
}

export enum OrderLifecycle {
    Ordered = "Ordered",
    OrderedArrivingGte7Days = "OrderedArrivingGte7Days",
    OrderedArrivingGte2Day = "OrderedArrivingGte2Day",
    OrderedArrivingTomorrow = "OrderedArrivingTomorrow",
    OrderedArrivingSameDay = "OrderedArrivingSameDay",
    ShippedArrivingGte7Days = "ShippedArrivingGte7Days",
    ShippedArrivingGte2Days = "ShippedArrivingGte2Days",
    ShippedArrivingTomorrow = "ShippedArrivingTomorrow",
    ShippedArrivingSameDay = "ShippedArrivingSameDay",
    ShippedArrivingUnknown = "ShippedArrivingUnknown",
    DeliveredEarly = "DeliveredEarly",
    DeliveredSameDay = "DeliveredSameDay",
    DeliveredYesterday = "DeliveredYesterday",
    DeliveredGte2Days = "DeliveredGte2Days",
    DeliveredGte6Months = "DeliveredGte6Months",
    Cancelled = "Cancelled",
    DelayedMiss1 = "DelayedMiss1",
    ShippedActualEtaUnknown = "ShippedActualEtaUnknown",
    ShippedActualEtaNextWeekUnknown = "ShippedActualEtaNextWeekUnknown",
    DeliveredUnknown = "DeliveredUnknown",
    Unknown = "Unknown"
}

export interface ItemGroupSummary {
    id: string;
    secondaryId?: string;
    headline: string;
    headlineAbbr: string;
    subline: string;
    orderingChannelLine: string;
    note: string;
    pillPackGroupSummary: PackagingGroupSummary;
    bottleGroupSummary: PackagingGroupSummary;
    itemGroups: ItemGroup[];
    address: Address;
    orderDate: HumanizedDate;
    orderStatus: OrderLifecycle; 
    isRebillOrder: Boolean;
    isPickUpOrder:Boolean;
    bypassTracker: boolean;
    annotations?: Annotation[];
}

export interface PackagingGroupSummary {
    id: string;
    headline: string;
    subline: string;
    startConsumptionAtString?: string;
    itemGroups: ItemGroup[];
    imageUrl: string;
    groupType: string;
    annotations?: Annotation[];
}

export interface ItemGroup {
    annotations: Annotation[];
    id: string;
    headline: string;
    itemSummaries: PurchasedItemSummary[];
}

export interface PurchasedItemSummary {
    id: string;
    heading: Heading;
    orderId: string;
    orderItemId: string;
    purchaseId: string;
    rxInfo: Rx;
    isAutoRefill?: boolean;
    deliveryDate?: string;
    annotations: Annotation[];
    showShiplit?: boolean;
}

export interface Heading {
    headline: string;
    subline?: string;
}

// TODO: remove drugType. SIM: https://sim.amazon.com/issues/potux-1760
export interface Rx {
    id: string;
    imageUrl: string;
    drugType?: DrugType;
    daysSupply: string;
    quantity: {
        number: string;
        unit: string;
    };
    headline: string;
    subline: string;
    autoRefillLine: string;
    asin: string;
    prescriber: string;
    filledOn: HumanizedDate;
    nabp: string;
    ndc11: string;
    prescriptionNumber: string;
}

export interface RedirectCancellationMessages {
    cancellationSuccessRedirectTitle: String
    cancellationPendingRedirectTitle: String
    cancellationPendingAutoRefillRedirectTitle: String
    cancellationPendingAutoRefillRedirectMessage: String
    cancellationSuccessAutoRefillRedirectMessage: String
}


export interface CancelFormLabels {
    cancelItemInThisOrder: String
    cancelButton: String
    cancelFormTitle: String
    cancelFormMessage: String
    cancelPending: String
    cancelFailed: String
    cancelSuccess: String
    cancelTitle: String
    pillPackCancelIngress: string
    pillPackCancelModalTitle: string
    pillPackCancelModalBody: string
    cancellationSuccessHeadlineSingular: string
    cancellationSuccessHeadlinePlural: string
    cancellationPendingHeadline: string
    cancellationPendingSubline: string
    cancelReason: CancelReason
}

export interface CancelReason {
    cancelReasonLabel: string
    cancelReasonSelectLabel: string
    cancelReasonFilterOptions: CancellationReasonFilterOption[]
}

export interface CancellationReasonFilterOption {
    key: string
    value: string
}

export interface RefundBottomSheetDetails {
    refundDetails: string;
    refundTotalRow: RefundRow;
    refundDateGroups: RefundDateGroup[];
}

export interface RefundDateGroup {
    refundDate: string;
    refundBreakdowns: RefundBreakdown[];
}

export interface RefundBreakdown {
    itemRefunds: RefundRow[];
    shippingRefund?: RefundRow;
    otherRefund?: RefundRow;
    taxRefund?: RefundRow;
}

export interface RefundRow {
    refundLabel: string;
    refundAmount: RefundAmount; // Refund amount is an already existing object that contains amount and currency.
}

export interface Refund {
    reversalId: string
    creationDate: string
    refundTotal: RefundAmount
    taxRefundTotal: RefundAmount
    refundMonetaryComponents: RefundMonetaryComponent[]
}

export interface RefundMonetaryComponent {
    name: string
    nameLabel: string
    baseAmount: RefundAmount
    taxAmount: RefundAmount
    totalAmount: RefundAmount
}

export interface RefundAmount {
    amount: string
    currency: string
}

export interface TrackingPageLabels {
    tracking: string
    orderSummary: string
    deliveryInformation: string
    timeAlert: string
    reportDamagedOrMissingMedications: ReportDamagedOrMissingMedications
    viewShipmentLiterature?: string;
    downloadingShipmentLiterature?: string;
    downloadingDoneShipmentLiterature?: string;
    downloadingErrorShipmentLiterature?: string;
    continueShipmentLiterature?: string;
    medicationInformationShipmentLiterature?: string;
    pdfNameShipmentLiterature?: string;
}

export interface OrderTrackerStep {
    label: string
    completionDate: string
    status: string
}

export interface ReportDamagedOrMissingMedications {
    title: string
    content: string
}

export interface OrderTracker {
    headline: string
    subline: string
    labels: TrackingPageLabels
    trackerSteps: OrderTrackerStep[]
    delivery?: Delivery
    isPillPackComDelivery?: boolean
}

export interface PickUpPage {
  labels: PickupPageLabels
  qrCode: PickupPageQRCode
  instructions: PickupPageInstructions
}

export interface PickupPageQRCode {
  qrString: string
}

export interface PickupPageLabels {
  kioskScanQRMsg: string
  kioskPickUpMsg: string
  kioskOrderHelpLabel: string
  kioskOrderHelpMsg: string
  kioskPickUpInstructionsLabel: string
  kioskCustomerCareLabel: string
  amazonPharmacyKiosk: string
  missedPickUpMsg: string
  missedPickUpRetry: string
  orderSummary: string
  kioskQrOrderStillProcessing: string
  kioskQrWaiting: string
  kioskQrOrderTakingLonger: string
  kioskQrCustomerCare: string
  kioskQrCodeUnavailable: string
  kioskQrTryAgain: string
  backToOneMedicalLabel: string
}

export interface PickupPageInstructions {
  kioskCancelRefundMsg: string
  kioskPickUpAddress: string
  kioskPickUpHours: string
  missedPickUpDetails: string
}

export interface PaymentLabels {
    paymentDeclineAlertTitle: string
    paymentDeclineAlertText: string
    addPaymentMethodTitle: string
    addPaymentMethodText: string
    editPaymentMethodText: string
    backupPaymentMethodLearnMore: string
    paymentMethodUpdatedText: string
    paymentMethodUpdatedTitle: string
}

export type CancellationStatus = 'success' | 'pending';

export interface CancellationResult {
    status: CancellationStatus;
    cancelledCount: number;
}

export interface Alert {
    article: Article;
    alertType: 'PAYMENT' | 'REFUND' | 'REPLACEMENT' | 'CANCEL' | 'REVERSAL' | 'PAYMENT_METHOD_UPDATE' | 'PILL_PACK_COM' | 'CANCELLATION_SUCCESS' | 'CANCELLATION_PENDING';
}

export interface Article {
    headline: string;
    sublines: string[];
}

export interface DigitalShiplitBottomSheetProps {
    labels: DigitalShiplitPageLabels;
    isIosDevice?: boolean;
    isAndroidDevice?: boolean;
    isMShop?: boolean
    bottomSheetRef?: React.Ref<any>;
    currentPage?: DigitalShiplitPageContext;
}

export interface DigitalShiplitPageLabels {
    viewShipmentLiterature?: string
    downloadingShipmentLiterature?: string
    downloadingDoneShipmentLiterature?: string
    downloadingErrorShipmentLiterature?: string
    continueShipmentLiterature?: string
    medicationInformationShipmentLiterature?: string
    pdfNameShipmentLiterature?: string
}

export enum ShiplitStatus {
    Hidden,
    Downloading,
    Done,
    Error
}

export enum DigitalShiplitPageContext {
    OrderTracker = "orderTracker",
    OrderSummary = "orderSummary",
    DirectAccess = "directAccess"
}

export interface DigitalShiplitLinkProps {
    orderKeyId?: string;
    showShiplit?: boolean;
    currentLinkNumber?: number;
    labels: DigitalShiplitPageLabels;
    isIosDevice?: boolean;
    isAndroidDevice?: boolean;
    isMShop?: boolean
    onShiplitClick?: (orderKeyId: string) => void;
}
